import React from "react";
import { Box, Typography } from "@mui/material";
import { CREATE_CUSTOMER_STEPS } from "./stepsConfig";

/**
 * Step indicator with clickable circles. Step numbers are always in circles.
 * Only the circle is clickable; navigation/validation is handled by parent via onStepClick.
 */
const Stepper = ({ activeStep, onStepClick }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 1,
        py: 2,
        px: 0,
        mb: 2,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      {CREATE_CUSTOMER_STEPS.map((step) => {
        const isActive = activeStep === step.id;
        const isCompleted = step.id < activeStep;
        const isInactive = step.id > activeStep;
        const stepNumber = step.id;
        const isClickable = stepNumber <= activeStep && typeof onStepClick === "function";

        const circleSx = {
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          fontSize: "1rem",
          transition: "background-color 0.2s, border-color 0.2s, box-shadow 0.2s, opacity 0.2s",
          border: "2px solid",
          p: 0,
          m: 0,
          font: "inherit",
          outline: "none",
          "&:focus-visible": isClickable
            ? {
                outline: "2px solid",
                outlineOffset: 2,
                outlineColor: "primary.main",
              }
            : { outline: "none" },
          ...(isActive && {
            bgcolor: "primary.main",
            color: "primary.contrastText",
            borderColor: "primary.main",
            ...(isClickable && {
              "&:hover": {
                bgcolor: "primary.dark",
                borderColor: "primary.dark",
                boxShadow: 1,
              },
            }),
          }),
          ...(isCompleted && {
            bgcolor: "action.selected",
            color: "primary.main",
            borderColor: "primary.light",
            ...(isClickable && {
              "&:hover": {
                bgcolor: "action.hover",
                borderColor: "primary.main",
                boxShadow: 1,
              },
            }),
          }),
          ...(isInactive && {
            bgcolor: "action.hover",
            color: "text.secondary",
            borderColor: "divider",
            opacity: isClickable ? 1 : 0.6,
            ...(isClickable && {
              "&:hover": {
                bgcolor: "action.selected",
                borderColor: "primary.light",
                boxShadow: 1,
              },
            }),
          }),
        };

        const handleCircleClick = () => {
          if (isClickable) {
            onStepClick(stepNumber);
          }
        };

        return (
          <Box
            key={step.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: 1,
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={handleCircleClick}
              disabled={!isClickable}
              sx={{
                ...circleSx,
                cursor: isClickable ? "pointer" : "default",
                "&:disabled": {
                  cursor: "default",
                  pointerEvents: "none",
                },
              }}
              aria-label={`Krok ${stepNumber}: ${step.label}`}
              aria-current={isActive ? "step" : undefined}
              aria-disabled={!isClickable}
            >
              {stepNumber}
            </Box>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                textAlign: "center",
                fontWeight: isActive ? 600 : 400,
                color: isActive ? "primary.main" : "text.secondary",
                pointerEvents: "none",
              }}
            >
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default Stepper;
